import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { fetchWithAuth } from '../utils/api';
import { useLanguage } from './LanguageContext';
import { calculateLine, resolveVatRate } from '../utils/vat';

export const BLANK_SALE_LINE = {
  product_id: '', quantity: '1', unit_price: '', vat_mode: 'none', vat_rate: '',
  discount_amount: '0',
};

const ProductSaleEditor = ({ value = [], onChange, branchId, branchVatRate = 7, disabled = false }) => {
  const { print_lang, currentLanguage } = useLanguage();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (!branchId) return;
    fetchWithAuth(`/api/shop/products?branch_id=${branchId}&status=active`)
      .then((r) => setProducts(r?.data || [])).catch(() => setProducts([]));
  }, [branchId]);

  const productById = useMemo(
    () => new Map(products.map((p) => [String(p.product_id), p])),
    [products]
  );

  const productLabel = (p) =>
    currentLanguage === 'th' ? (p.name_th || p.name) : p.name;

  const patch = (idx, changes) =>
    onChange(value.map((l, i) => (i === idx ? { ...l, ...changes } : l)));

  const addLine = () => onChange([...value, { ...BLANK_SALE_LINE }]);
  const removeLine = (idx) => onChange(value.filter((_, i) => i !== idx));

  const chooseProduct = (idx, productId) => {
    const p = productById.get(String(productId));
    patch(idx, {
      product_id: productId,
      unit_price: p ? p.sale_price : '',
      vat_mode: p ? p.vat_mode : 'none',
      vat_rate: p && p.vat_rate !== null ? p.vat_rate : '',
    });
  };

  const lineTotal = (line) => {
    if (line.unit_price === '' || line.unit_price === undefined) return 0;
    const rate = resolveVatRate({
      vatMode: line.vat_mode, itemRate: line.vat_rate, branchRate: branchVatRate,
    });
    return calculateLine({
      quantity: line.quantity || 0,
      unit_price: line.unit_price,
      discount_amount: line.discount_amount || 0,
      vat_mode: line.vat_mode,
      vat_rate: rate,
    }).total;
  };

  const grandTotal = value.reduce((s, l) => s + lineTotal(l), 0);

  const stockWarning = (line) => {
    const p = productById.get(String(line.product_id));
    if (!p) return null;
    return Number(line.quantity) > Number(p.stock_quantity)
      ? `${print_lang('insufficient_stock', 'Not enough stock')} (${p.stock_quantity})`
      : null;
  };

  return (
    <div>
      {value.length === 0 && (
        <p className="text-sm text-gray-500 mb-2">
          {print_lang('no_products_on_note', 'No products sold')}
        </p>
      )}

      {value.map((line, idx) => {
        const warn = stockWarning(line);
        return (
          <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-end">
            <div className="col-span-5">
              <label className="block text-xs text-gray-500 mb-1">
                {print_lang('product', 'Product')}
              </label>
              <select className="w-full border rounded px-2 py-1" disabled={disabled}
                      value={line.product_id}
                      onChange={(e) => chooseProduct(idx, e.target.value)}>
                <option value="">-</option>
                {products.map((p) => (
                  <option key={p.product_id} value={p.product_id}>
                    {productLabel(p)}{p.unit ? ` (${p.unit})` : ''}
                  </option>
                ))}
              </select>
              {warn && <p className="text-xs text-red-600 mt-1">{warn}</p>}
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">
                {print_lang('quantity', 'Quantity')}
              </label>
              <input type="number" step="1" min="1" className="w-full border rounded px-2 py-1"
                     disabled={disabled} value={line.quantity}
                     onChange={(e) => patch(idx, { quantity: e.target.value })} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">
                {print_lang('unit_price', 'Unit Price')}
              </label>
              <input type="number" step="0.01" min="0" className="w-full border rounded px-2 py-1"
                     disabled={disabled} value={line.unit_price}
                     onChange={(e) => patch(idx, { unit_price: e.target.value })} />
            </div>
            <div className="col-span-2 text-right">
              <label className="block text-xs text-gray-500 mb-1">{print_lang('total', 'Total')}</label>
              <div className="px-2 py-1 font-medium">{lineTotal(line).toFixed(2)}</div>
            </div>
            <div className="col-span-1 flex justify-end">
              <button type="button" className="p-2 hover:bg-red-50 rounded" disabled={disabled}
                      onClick={() => removeLine(idx)} title={print_lang('delete', 'Delete')}>
                <Trash2 size={16} className="text-red-600" />
              </button>
            </div>
          </div>
        );
      })}

      <div className="flex justify-between items-center mt-3">
        <button type="button" className="btn flex items-center gap-2" disabled={disabled}
                onClick={addLine}>
          <Plus size={16} /> {print_lang('add_product_line', 'Add Product')}
        </button>
        {value.length > 0 && (
          <div className="text-right">
            <span className="text-sm text-gray-500 mr-2">{print_lang('total', 'Total')}</span>
            <span className="font-bold">{grandTotal.toFixed(2)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductSaleEditor;
